import { useRef, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import styled from 'styled-components';

import { useVideoProjectCreation } from '@contexts/VideoProjectCreation.context';
import { displayToNativeCoordinates, nativeToDisplayCoordinates } from '@utils/video/helpers';
import VirtualLineCanvas from '@components/video/VirtualLineCanvas';

const InteractionCanvas = styled.canvas`
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	z-index: 11;
	cursor: ${({ $drawMode }) => ($drawMode ? 'crosshair' : 'default')};
	pointer-events: ${({ $drawMode }) => ($drawMode ? 'auto' : 'none')};

	${({ $drawMode }) =>
		$drawMode &&
		`
		box-shadow: inset 0 0 0 3px rgba(25, 135, 84, 0.5);
	`}
`;

const CanvasOverlay = ({
	lineColor = '#198754',
	drawMode = false,
	onDrawModeChange,
	startPoint = null,
	onStartPointChange,
	endPoint = null,
	onEndPointChange,
}) => {
	const interactionCanvasRef = useRef(null);
	const { videoRef } = useVideoProjectCreation();
	const { watch, setValue } = useFormContext();

	const [error, setError] = useState(null);

	const virtualLineDefinition = watch('virtualLineDefinition');

	// Load existing line from form
	useEffect(() => {
		if (virtualLineDefinition) {
			onStartPointChange?.(virtualLineDefinition.start_point);
			onEndPointChange?.(virtualLineDefinition.end_point);
		} else {
			onStartPointChange?.(null);
			onEndPointChange?.(null);
		}
	}, [virtualLineDefinition, onStartPointChange, onEndPointChange]);

	// Sync interaction canvas size with video
	useEffect(() => {
		if (!drawMode) return;

		try {
			const video = videoRef.current;
			const canvas = interactionCanvasRef.current;
			if (!video || !canvas) return;

			const syncSize = () => {
				if (video.videoWidth === 0 || video.videoHeight === 0) return;
				const rect = video.getBoundingClientRect();
				canvas.width = rect.width;
				canvas.height = rect.height;
			};

			syncSize();
			const resizeObserver = new ResizeObserver(syncSize);
			resizeObserver.observe(video);
			return () => resizeObserver.disconnect();
		} catch (err) {
			console.error('Canvas sync error:', err);
			setError('Canvas initialization failed');
		}
	}, [drawMode, videoRef]);

	// Clear interaction canvas when draw mode ends or line is committed
	useEffect(() => {
		if (!drawMode || endPoint) {
			const canvas = interactionCanvasRef.current;
			const ctx = canvas?.getContext('2d');
			if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
		}
	}, [drawMode, endPoint]);

	const handleCanvasClick = e => {
		if (!drawMode) return;

		const canvas = interactionCanvasRef.current;
		const video = videoRef.current;
		const rect = canvas.getBoundingClientRect();

		const displayX = e.clientX - rect.left;
		const displayY = e.clientY - rect.top;

		const [nativeX, nativeY] = displayToNativeCoordinates(displayX, displayY, video, canvas);

		if (!startPoint) {
			onStartPointChange?.([nativeX, nativeY]);
		} else if (!endPoint) {
			onEndPointChange?.([nativeX, nativeY]);

			const lineDefinition = {
				start_point: [startPoint[0], startPoint[1]],
				end_point: [nativeX, nativeY],
			};

			setValue('virtualLineDefinition', lineDefinition);
			onDrawModeChange?.(false);
		}
	};

	const handleMouseMove = e => {
		if (!drawMode || !startPoint || endPoint) return;

		const canvas = interactionCanvasRef.current;
		const video = videoRef.current;
		const rect = canvas.getBoundingClientRect();

		const displayX = e.clientX - rect.left;
		const displayY = e.clientY - rect.top;

		const [displayStartX, displayStartY] = nativeToDisplayCoordinates(
			startPoint[0],
			startPoint[1],
			video,
			canvas
		);

		const ctx = canvas.getContext('2d');
		ctx.clearRect(0, 0, canvas.width, canvas.height);

		ctx.strokeStyle = lineColor;
		ctx.lineWidth = 3;
		ctx.globalAlpha = 0.5;
		ctx.setLineDash([5, 5]);
		ctx.beginPath();
		ctx.moveTo(displayStartX, displayStartY);
		ctx.lineTo(displayX, displayY);
		ctx.stroke();
		ctx.setLineDash([]);
		ctx.globalAlpha = 1.0;
	};

	if (error) {
		return <div style={{ padding: '1rem', color: '#dc3545' }}>{error}</div>;
	}

	return (
		<>
			<VirtualLineCanvas
				videoElement={videoRef.current}
				startPoint={startPoint}
				endPoint={endPoint}
				lineColor={lineColor}
			/>
			<InteractionCanvas
				ref={interactionCanvasRef}
				onClick={handleCanvasClick}
				onMouseMove={handleMouseMove}
				$drawMode={drawMode}
			/>
		</>
	);
};

export default CanvasOverlay;
